import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

import CommunityFeedbackPlacement from './community-feedback-placement'

import type { NonFsdsGeneralV1FeedbackDialog } from '@/types/non-fsds-general-v1.type'

interface GeneralV1HeroSectionProps {
  title: string
  description: string
  ctaLabel: string
  ctaUrl?: string
  heroImage?: string
  focalPoint?: { x: number; y: number }
  learnerCount?: string
  learnerLabel?: string
  learnerAvatars?: Array<{ image: string; alt?: string }>
  isCourseEnrolled: boolean
  isEnglish: boolean
  feedbackDialog?: NonFsdsGeneralV1FeedbackDialog
  className?: string
}

/**
 * General V1 Hero Section — 1440×810 viewport, Figma-faithful.
 *
 * Content sits at bottom-left with a single CTA and an informational learner
 * badge. Community feedback renders as the next homepage section. Scroll
 * animates to an 8px inset and 48px radius via clip-path (no layout thrash).
 */
export default function GeneralV1HeroSection({
  title,
  description,
  ctaLabel,
  ctaUrl = TEMPLATE_DEFAULT_URLS.learningPath,
  heroImage,
  focalPoint,
  learnerCount,
  learnerLabel,
  learnerAvatars,
  isCourseEnrolled: _isCourseEnrolled,
  isEnglish,
  feedbackDialog,
  className,
}: GeneralV1HeroSectionProps) {
  const sectionRef = React.useRef<HTMLElement>(null)

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    let ticking = false
    const handleScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const progress = Math.min(window.scrollY / 300, 1)
        const inset = progress * 8
        const radius = progress * 48
        if (sectionRef.current) {
          sectionRef.current.style.clipPath = `inset(${inset}px round ${radius}px)`
        }
        ticking = false
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const heroAlt = isEnglish ? 'Learners and mentors collaborating' : 'Học viên và mentor đang cộng tác'

  const isCtaDisabled = !ctaUrl || ctaUrl === '#'
  const focalX = Math.min(Math.max(focalPoint?.x ?? 0.5, 0), 1) * 100
  const focalY = Math.min(Math.max(focalPoint?.y ?? 0.5, 0), 1) * 100

  return (
    <>
      <section
        ref={sectionRef}
        data-testid="gv1-hero-section"
        className={cn(
          'relative flex h-[var(--gv1-hero-height)] items-end overflow-hidden bg-[var(--gv1-bg-primary)]',
          className
        )}
      >
        {/* Background media */}
        {heroImage ? (
          <Image
            src={heroImage}
            alt={heroAlt}
            fill
            priority
            quality={90}
            className="object-cover"
            sizes="100vw"
            style={{ objectPosition: `${focalX}% ${focalY}%` }}
          />
        ) : null}

        {/* Left gradient for readability */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, rgba(2,17,39,.56) 0%, rgba(2,17,39,.18) 48%, rgba(2,17,39,0) 78%)',
          }}
          data-testid="gv1-hero-left-overlay"
          aria-hidden="true"
        />
        {/* Bottom gradient for readability */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(0deg, rgba(2,17,39,.72) 0%, rgba(2,17,39,.16) 44%, rgba(2,17,39,0) 72%)',
          }}
          data-testid="gv1-hero-bottom-overlay"
          aria-hidden="true"
        />

        {/* Copy block — bottom-left */}
        <div className="relative z-10 w-full pb-[90px] px-4 sm:px-6 lg:px-[var(--gv1-header-px)]">
          <div className="max-w-[760px]">
            <h1
              className="text-5xl font-bold leading-[1.06] tracking-[-0.025em] text-[var(--gv1-text-on-dark)] sm:text-6xl lg:text-[64px]"
              style={{ fontFamily: 'var(--gv1-font-heading)' }}
            >
              {title}
            </h1>
            <p
              className="mt-4 max-w-[560px] text-[15px] leading-[22px] text-white/80"
              style={{ fontFamily: 'var(--gv1-font-body)' }}
            >
              {description}
            </p>

            {/* Single CTA */}
            <div className="mt-6">
              {isCtaDisabled ? (
                <button
                  disabled
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--gv1-accent)] px-6 text-sm font-semibold text-white opacity-50 cursor-not-allowed"
                >
                  {ctaLabel}
                  <ArrowRightIcon />
                </button>
              ) : (
                <Link
                  href={ctaUrl}
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--gv1-accent)] px-6 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gv1-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--gv1-bg-primary)]"
                >
                  {ctaLabel}
                  <ArrowRightIcon />
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Learner badge — informational only; feedback is a full section below. */}
        {(learnerCount || learnerLabel) && (
          <div
            className={cn(
              'absolute bottom-20 right-[var(--gv1-header-px)] z-10',
              'flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-white backdrop-blur-md'
            )}
            aria-label={[learnerCount, learnerLabel].filter(Boolean).join(' ')}
          >
            <TrendingUpIcon />
            <span className="text-sm font-semibold whitespace-nowrap">{learnerCount}</span>
            {learnerLabel && <span className="text-sm text-white/70 whitespace-nowrap">{learnerLabel}</span>}
            {learnerAvatars && learnerAvatars.length > 0 && (
              <div className="ml-1 flex -space-x-2">
                {learnerAvatars.slice(0, 5).map((avatar, idx) => (
                  <Image
                    key={idx}
                    src={avatar.image}
                    alt={avatar.alt || ''}
                    width={32}
                    height={32}
                    className="size-8 rounded-full border-2 border-white/70 object-cover"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {feedbackDialog && <CommunityFeedbackPlacement {...feedbackDialog} />}
    </>
  )
}

function ArrowRightIcon() {
  return (
    <svg
      className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
    </svg>
  )
}

function TrendingUpIcon() {
  return (
    <svg
      className="size-4 text-green-400"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
      />
    </svg>
  )
}
